import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useGetCurrentUserQuery } from "../store/api";
import { setCredentials } from "../store/slices/authSlice";
import { useApiError } from "../hooks/useApiError";
import Cookies from "js-cookie";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface AuthInitializerProps {
    children: React.ReactNode;
}

const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation("common");
    const token = Cookies.get("token");
    const crossAuthToken = searchParams.get("cross_auth_token");

    // Если cross_auth_token есть в URL и его нет в куки — сохраняем Bearer токен напрямую
    useEffect(() => {
        if (crossAuthToken && !token) {
            // Сохраняем Bearer токен напрямую
            Cookies.set("token", crossAuthToken, { expires: 7 });
            // Удаляем токен из URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete("cross_auth_token");
            const newUrl =
                window.location.pathname +
                (newSearchParams.toString()
                    ? `?${newSearchParams.toString()}`
                    : "") +
                window.location.hash;
            window.history.replaceState({}, "", newUrl);
            // После этого приложение само подхватит токен и выполнит обычную авторизацию
            window.location.reload();
        }
    }, [crossAuthToken, token, searchParams]);

    // Синхронизируем токен из куки с Redux store сразу при инициализации
    useEffect(() => {
        if (token) {
            dispatch(setCredentials({ user: null, token }));
        }
    }, [token, dispatch]);

    const {
        data: user,
        error,
        isLoading,
    } = useGetCurrentUserQuery(undefined, {
        skip: !token,
        pollingInterval: 60000,
    });

    useApiError(error);

    useEffect(() => {
        if (user && token) {
            // Расширяем объект пользователя произвольными полями без ошибок типов
            let userWithRoles: any = { ...user };
            if (!user.roles || user.roles.length === 0) {
                const cachedRoles = localStorage.getItem("userRoles");
                if (cachedRoles) {
                    try {
                        const parsedRoles = JSON.parse(cachedRoles);
                        userWithRoles.roles = parsedRoles;
                    } catch (e) {
                        console.error("Failed to parse cached roles:", e);
                    }
                }
            }
            if (!userWithRoles.language) {
                const cachedLanguage = localStorage.getItem("userLanguage");
                userWithRoles.language = cachedLanguage || "ru";
            }
            // Добавляем is_admin на основе roles, если не пришел с сервера
            if (userWithRoles.is_admin === undefined) {
                userWithRoles.is_admin =
                    userWithRoles.roles?.includes("admin") || false;
            }

            // Проверяем, есть ли у пользователя допустимая роль (admin или editor)
            const hasAllowedRole =
                userWithRoles.roles?.includes("admin") ||
                userWithRoles.roles?.includes("editor") ||
                false;

            if (!hasAllowedRole) {
                // Если нет допустимых ролей, очищаем токен и перенаправляем на страницу входа
                Cookies.remove("token");
                localStorage.removeItem("userRoles");
                localStorage.removeItem("userLanguage");
                dispatch(setCredentials({ user: null, token: null }));
                navigate("/signin?error=access_denied");
                return;
            }

            dispatch(setCredentials({ user: userWithRoles, token }));

            // Редирект на дашбоард после успешной авторизации
            if (
                window.location.pathname === "/" ||
                window.location.pathname === "/signin" ||
                window.location.pathname === "/signup"
            ) {
                navigate("/dashboard");
            }
        }
    }, [user, token, dispatch, navigate]);

    if (token && isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">
                        {t("loading")}
                    </p>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export default AuthInitializer;
